import { useState } from 'react';
import { cn } from '@/lib/utils';

const Skills = () => {
  const [activeTab, setActiveTab] = useState('technical');
  
  const skillSets = {
    technical: [
      { name: 'JavaScript', level: 90, icon: "🅙" },
      { name: 'TypeScript', level: 85, icon: "🆃" },
      { name: 'React', level: 90, icon: "⚛️" },
      { name: 'Node.js', level: 80, icon: "🄽" },
      { name: 'Python', level: 75, icon: "🅟" },
      { name: 'SQL', level: 70, icon: "🅢" },
      { name: 'GraphQL', level: 75, icon: "🄶" },
      { name: 'AWS', level: 65, icon: "☁️" }
    ],
    design: [
      { name: 'UI/UX Design', level: 80, icon: "🄳" },
      { name: 'Pixel Art', level: 85, icon: "🄿" },
      { name: 'Figma', level: 75, icon: "🅕" },
      { name: 'CSS/SASS', level: 90, icon: "🅒" },
      { name: 'Animation', level: 70, icon: "🄰" },
      { name: 'Responsive Design', level: 85, icon: "🅡" }
    ],
    soft: [
      { name: 'Problem Solving', level: 95, icon: "💡" },
      { name: 'Communication', level: 85, icon: "🗣️" },
      { name: 'Team Collaboration', level: 90, icon: "👥" },
      { name: 'Time Management', level: 80, icon: "⏱️" },
      { name: 'Adaptability', level: 85, icon: "🔄" },
      { name: 'Creativity', level: 90, icon: "🎨" }
    ]
  };

  type SkillCategoryKey = keyof typeof skillSets;
  
  const tabs = [
    { id: 'technical', label: 'Technical Skills' },
    { id: 'design', label: 'Design Skills' },
    { id: 'soft', label: 'Soft Skills' }
  ];

  const SkillBar = ({ name, level, icon }: { name: string; level: number; icon: string }) => (
    <div className="mb-6 animate-pixel-fade-in opacity-0" style={{ animationDelay: `${Math.random() * 0.5}s` }}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <span className="font-pixel w-8 h-8 flex items-center justify-center bg-forest text-white mr-3">{icon}</span>
          <span className="font-mono text-sm text-white">{name}</span>
        </div>
        <span className="font-pixel text-xs text-white">{level}%</span>
      </div>
      <div className="w-full h-4 bg-white/10 backdrop-blur-sm pixel-border overflow-hidden">
        <div 
          className="h-full bg-forest-light transition-all duration-1000 ease-out"
          style={{ 
            width: `${level}%`, 
            backgroundImage: 'linear-gradient(to right, #2d5d3b, #5ecc5e)' 
          }}
        ></div>
      </div>
    </div>
  );

  return (
    <section id="skills" className="py-20 relative">
      <div className="section-container">
        <h2 className="section-title text-white">My Skills</h2>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "pixel-btn py-2 px-4 text-xs",
                activeTab === tab.id 
                  ? "bg-forest text-white" 
                  : "bg-white/10 backdrop-blur-md text-white hover:bg-white/20"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
          {skillSets[activeTab as SkillCategoryKey].map((skill) => (
            <SkillBar 
              key={skill.name} 
              name={skill.name} 
              level={skill.level} 
              icon={skill.icon} 
            />
          ))}
        </div>
        
        {/* Decorative Pixels */}
        <div className="absolute -bottom-4 left-1/4 w-8 h-8 bg-grass pixel-border animate-float"></div>
        <div className="absolute -top-4 right-1/4 w-8 h-8 bg-water pixel-border animate-float" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/3 left-8 w-6 h-6 bg-flower-yellow pixel-border animate-float" style={{ animationDelay: '0.8s' }}></div>
        <div className="absolute bottom-1/3 right-8 w-6 h-6 bg-flower-purple pixel-border animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default Skills;